import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { SwitchControl } from '@euler/components/form/SwitchControl';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { usePersistFn } from '@euler/utils/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { memo, useCallback, useRef, useState } from 'react';
import {
  findNodeHandle,
  NativeSyntheticEvent,
  Platform,
  Text,
  TextInput,
  TextInputKeyPressEventData,
  TextInputSubmitEditingEventData,
  TouchableWithoutFeedback,
} from 'react-native';

type ActionItem = {
  type: 'move-up' | 'move-down' | 'remove' | 'close' | 'edit-issues';
  text: string;
};

const kActionItems: ActionItem[] = [
  { type: 'edit-issues', text: '编辑故障...' },
  { type: 'move-up', text: '上移' },
  { type: 'move-down', text: '下移' },
  { type: 'remove', text: '删除' },
  { type: 'close', text: '关闭' },
];

export const QuotationFormSubjectPanelHeader = memo(
  ({
    editable,
    index,
    title,
    checked,
    onCheckChange,
    canMoveUp,
    canMoveDown,
    onEditIssuesPress,
    onMoveUpPress,
    onMoveDownPress,
    onRemovePress,
    onNameChange,
    isNamePermitted,
  }: {
    editable?: boolean;
    index: number;
    title: string;
    checked?: boolean;
    onCheckChange?: (checked: boolean) => void;
    canMoveUp?: boolean;
    canMoveDown?: boolean;
    onMoveUpPress?: () => void;
    onMoveDownPress?: () => void;
    onRemovePress?: () => void;
    onEditIssuesPress?: () => void;
    onNameChange?: (name: string) => void;
    isNamePermitted: (name: string) => boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const [editMode, setEditMode] = useState(false);
    const [text, setText] = useState(title);
    const { showActionSheetWithOptions } = useActionSheet();
    const moreButtonRef = useRef(null);

    const onSubmitEditing = usePersistFn(
      (e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => {
        const updated = e.nativeEvent.text.trim();
        if (updated !== title) {
          if (isNamePermitted(updated)) {
            onNameChange?.(updated);
          } else {
            setText(title);
          }
        }
        setEditMode(false);
      },
    );

    const onKeyPress = usePersistFn(
      (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        if (Platform.OS !== 'web') return;

        if (e.nativeEvent.key === 'Enter') {
          const updated = text.trim();
          if (updated !== title) {
            if (isNamePermitted(updated)) {
              onNameChange?.(updated);
              setEditMode(false);
            }
          }
        } else if (e.nativeEvent.key === 'Escape') {
          setEditMode(false);
        }
      },
    );

    const onBlur = usePersistFn(() => {
      const updated = text.trim();
      if (updated !== title) {
        if (isNamePermitted(updated)) {
          onNameChange?.(updated);
        } else {
          setText(title);
        }
      }
      setEditMode(false);
    });

    const onMorePress = useCallback(() => {
      const items = kActionItems
        .filter(x => x.type !== 'move-up' || canMoveUp)
        .filter(x => x.type !== 'move-down' || canMoveDown);

      let nodeId: number | null = null;
      if (moreButtonRef.current != null) {
        nodeId = findNodeHandle(moreButtonRef.current);
      }

      showActionSheetWithOptions(
        {
          options: items.map(x => x.text),
          anchor: nodeId ?? undefined,
          cancelButtonIndex: items.findIndex(x => x.type === 'close'),
          destructiveButtonIndex: items.findIndex(x => x.type === 'remove'),
        },
        async buttonIndex => {
          if (buttonIndex == null) return;
          const item = items[buttonIndex] as ActionItem | undefined;
          if (item == null) return;
          if (item.type === 'move-up') {
            onMoveUpPress?.();
          } else if (item.type === 'move-down') {
            onMoveDownPress?.();
          } else if (item.type === 'remove') {
            onRemovePress?.();
          } else if (item.type === 'edit-issues') {
            onEditIssuesPress?.();
          }
        },
      );
    }, [
      canMoveDown,
      canMoveUp,
      onEditIssuesPress,
      onMoveDownPress,
      onMoveUpPress,
      onRemovePress,
      showActionSheetWithOptions,
    ]);

    return (
      <>
        <StackLayout direction="row" alignItems="center" flex={1} gap={rpx(10)}>
          <StackLayout direction="row" alignItems="center" flex={1}>
            <Text
              css={`
                font-size: ${rpx(28)}px;
                font-weight: bold;
                font-style: italic;
                color: #0f4884;
              `}
            >{`${index + 1}. `}</Text>
            {editMode ? (
              <TouchableWithoutFeedback>
                <TextInput
                  value={text}
                  placeholder="请输入报价项目名称"
                  autoFocus={true}
                  selectTextOnFocus={true}
                  onChangeText={setText}
                  onSubmitEditing={onSubmitEditing}
                  onKeyPress={onKeyPress}
                  onBlur={onBlur}
                  css={`
                    font-family: ${FontFamily.NotoSans.Medium};
                    font-size: ${rpx(23)}px;
                    border-width: 0;
                  `}
                />
              </TouchableWithoutFeedback>
            ) : (
              <HStack
                justifyContent="flex-start"
                alignItems="center"
                flex={1}
                css={`
                  overflow: hidden;
                `}
              >
                <Typography.Title
                  level={3}
                  size={rpx(21)}
                  medium
                  color="#0f4884"
                  numberOfLines={0}
                  flexBasis={'auto'}
                  flexShrink={1}
                >
                  {title}
                </Typography.Title>
                {editable && (
                  <Center.Touchable
                    hitSlop={PredefinedHitSlops.extraLarge}
                    onPress={() => setEditMode(true)}
                    ml={rpx(5)}
                    flexShrink={0}
                  >
                    <Icon
                      name="note"
                      size={rpx(19)}
                      color="#777"
                      css={`
                        top: ${rpx(1.5)}px;
                      `}
                    />
                  </Center.Touchable>
                )}
              </HStack>
            )}
          </StackLayout>
        </StackLayout>
        {editable && (
          <StackLayout
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={rpx(8)}
            ml={rpx(10)}
          >
            <Center.Touchable
              onPress={onMorePress}
              hitSlop={PredefinedHitSlops.large}
            >
              <Icon
                name="more-vertical"
                size={rpx(20)}
                style={{
                  transform: [{ rotate: '90deg' }],
                  color: '#777',
                }}
              />
            </Center.Touchable>
            <SwitchControl
              theme="primary"
              rounded
              size={{ thumb: rpx(18), margin: 1, borderRadius: 0 }}
              on={checked}
              onChange={onCheckChange}
              css={`
                margin-left: ${rpx(10)}px;
              `}
            />
          </StackLayout>
        )}
      </>
    );
  },
);
